import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Tabs } from 'vnetwork-ui'

// Components
import SubHeaders from '@/components/Headers/SubHeaders'
import useTabs from '@/hooks/useTabs'

// Services and Utils
import { paths } from '@/utils/path'
import AllAssets from './AllAssets'
import MyAssets from './MyAssets'
import PendingApproval from './PendingApproval'

import DraftsTab from './DraftsTab'

import { useTourHandler } from '@/contexts/tour-handler'
import { useAssetListTour } from './use-asset-list-tour'

const AssetList: React.FC = () => {
    const { t } = useTranslation('asset')
    const navigate = useNavigate()

    // Tour
    const { startTour } = useAssetListTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const tabs = [
        {
            label: t('tabs.list'),
            value: 'list',
            component: <AllAssets />,
        },
        {
            label: t('tabs.myAssets'),
            value: 'myAssets',
            component: <MyAssets />,
        },

        {
            label: t('statuses.draft'),
            value: 'drafts',
            component: <DraftsTab />,
        },
        {
            label: t('tabs.pendingApproval'),
            value: 'pendingApproval',
            component: <PendingApproval />,
        },
    ]

    const { TabComponent, TabProps } = useTabs({
        tabs,
        clearFilters: true,
    })

    return (
        <>
            <SubHeaders
                actions={[
                    {
                        label: t('actions.create'),
                        variant: 'contained',
                        onClick: () => navigate(paths.assetCreate),
                        'data-tour': 'create-asset-btn',
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.assetsList,
                        title: t('assetList'),
                    },
                ]}
            />
            <div data-tour="asset-tabs">
                <Tabs {...TabProps} />
            </div>
            {TabComponent}
        </>
    )
}

export default AssetList
